import React from 'react';
import { Link } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import PaginationControls from './PaginationControls';

export interface Column<T> {
  key: string;
  header: string | React.ReactNode;
  render: (item: T, index: number) => React.ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
}

export interface EmptyState {
  icon: React.ElementType;
  title: string;
  description: string;
  action?: {
    label: string;
    onClick?: () => void;
    component?: 'button' | 'link';
    to?: string;
  };
}

export interface PaginationConfig {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  keyExtractor: (item: T) => string | number;
  title?: string | React.ReactNode;
  titleCount?: number;
  emptyState?: EmptyState;
  pagination?: PaginationConfig;
  loading?: boolean;
  showLoadingInHeader?: boolean;
  rowClassName?: string | ((item: T, index: number) => string);
  headerActions?: React.ReactNode;
  className?: string;
}

function DataTable<T>({
  data,
  columns,
  keyExtractor,
  title,
  titleCount,
  emptyState,
  pagination,
  loading = false,
  showLoadingInHeader = false,
  rowClassName,
  headerActions,
  className = '',
}: DataTableProps<T>) {
  // Show full-page spinner for initial load
  const isInitialLoad = loading && data.length === 0;
  
  if (isInitialLoad) {
    return <LoadingSpinner />;
  }

  // Empty state
  if (data.length === 0 && emptyState) {
    const EmptyIcon = emptyState.icon;
    return (
      <div className={`bg-white shadow rounded-lg ${className}`}>
        <div className="px-4 py-5 sm:p-6">
          {title && (
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {title}
              {titleCount !== undefined && ` (${titleCount})`}
            </h3>
          )}
          <div className="text-center py-8">
            <EmptyIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {emptyState.title}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {emptyState.description}
            </p>
            {emptyState.action && (
              <div className="mt-6">
                {emptyState.action.component === 'link' && emptyState.action.to ? (
                  <Link
                    to={emptyState.action.to}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    {emptyState.action.label}
                  </Link>
                ) : (
                  <button
                    onClick={emptyState.action.onClick}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    {emptyState.action.label}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Get alignment classes
  const getAlignmentClasses = (align?: 'left' | 'right' | 'center') => {
    switch (align) {
      case 'right':
        return 'text-right';
      case 'center':
        return 'text-center';
      case 'left':
      default:
        return 'text-left';
    }
  };

  // Get row class name
  const getRowClassName = (item: T, index: number) => {
    const baseClass = 'hover:bg-gray-50';
    if (typeof rowClassName === 'function') {
      return `${baseClass} ${rowClassName(item, index)}`;
    }
    return rowClassName ? `${baseClass} ${rowClassName}` : baseClass;
  };

  return (
    <div className={`bg-white shadow rounded-lg ${className}`}>
      {(title || headerActions) && (
        <div className={`px-4 py-5 sm:p-6 ${pagination ? '' : 'border-b border-gray-200'}`}>
          <div className="flex items-center justify-between mb-4">
            {title && (
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <span>
                  {title}
                  {titleCount !== undefined && ` (${titleCount})`}
                </span>
                {showLoadingInHeader && loading && (
                  <div className="ml-2 animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500"></div>
                )}
              </h3>
            )}
            {headerActions && <div>{headerActions}</div>}
          </div>
        </div>
      )}

      {data.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`px-6 py-3 ${getAlignmentClasses(column.align)} text-xs font-medium text-gray-500 uppercase tracking-wider ${column.className || ''}`}
                    >
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((item, index) => (
                  <tr key={keyExtractor(item)} className={getRowClassName(item, index)}>
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={`px-6 py-4 whitespace-nowrap ${getAlignmentClasses(column.align)} ${column.className || ''}`}
                      >
                        {column.render(item, index)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && pagination.totalPages > 1 && (
            <PaginationControls
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              itemsPerPage={pagination.itemsPerPage}
              onPageChange={pagination.onPageChange}
            />
          )}
        </>
      )}
    </div>
  );
}

export default DataTable;

